/**
 * The HTTP client every call in the dashboard goes through.
 *
 * Mirrors the app's client deliberately: the same `Result` shape, the same refresh-before-expiry
 * behaviour, and the same rule that a failure is a value rather than an exception. Two clients that
 * disagree about what a 401 means is how an operator ends up signed out mid-event on one surface
 * and not the other.
 */

export type Ok<T> = { ok: true; value: T }
export type Err = { ok: false; error: Failure }
export type Result<T> = Ok<T> | Err

export interface Failure {
  /** The server's stable identifier, which is what code branches on. */
  code: string
  /** Text fit to show somebody. */
  message: string
  status: number
  /**
   * The server's request id, echoed from the error body or the X-Request-Id header.
   *
   * Worth surfacing: quoting it turns "it failed" into a single grep on the server, which is the
   * difference between a report that can be actioned and one that cannot.
   */
  requestId?: string
  /** Field name to message, so a form can mark the offending inputs rather than showing a banner. */
  violations?: Record<string, string>
}

export interface Session {
  accessToken: string
  refreshToken: string
  accessTokenExpiresAt: string
  userId: string
}

const SESSION_KEY = 'tacmaps.session'
const SERVER_KEY = 'tacmaps.server'

/**
 * Told when the session has ended and cannot be recovered.
 *
 * The client cannot navigate, and the phase machine cannot see inside a fetch, so without this a
 * dead session is invisible: every request 401s, each caller shows its own error, and the polling
 * carries on presenting a token the server has already rejected. That is what it did -- six
 * requests every five seconds, forever.
 */
let onSessionEnded: (() => void) | null = null

export function setSessionEndedHandler(handler: (() => void) | null) {
  onSessionEnded = handler
}

function endSession() {
  const had = localStorage.getItem(SESSION_KEY) !== null
  setSession(null)
  // Only announce a session that existed, or signing out by hand would bounce off this too.
  if (had) onSessionEnded?.()
}

export function storedServer(): string {
  return localStorage.getItem(SERVER_KEY) ?? ''
}

export function setServer(url: string) {
  localStorage.setItem(SERVER_KEY, normaliseServerUrl(url))
}

export function storedSession(): Session | null {
  const raw = localStorage.getItem(SESSION_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as Session
  } catch {
    // A corrupt entry means a sign-in, not a crash.
    localStorage.removeItem(SESSION_KEY)
    return null
  }
}

export function setSession(session: Session | null) {
  if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  else localStorage.removeItem(SESSION_KEY)
}

/**
 * Work out the scheme somebody meant when they did not type one.
 *
 * Assuming https everywhere is wrong here for the same reason it is in the app: most instances are
 * a machine on a club's own network with no certificate and no domain to put one on.
 */
export function normaliseServerUrl(raw: string): string {
  let value = raw.trim()
  if (!value) return value
  if (!/^https?:\/\//i.test(value)) {
    value = `${looksLocal(value) ? 'http' : 'https'}://${value}`
  }
  return value.replace(/\/+$/, '')
}

function looksLocal(hostAndPort: string): boolean {
  const host = hostAndPort.split('/')[0].split(':')[0].toLowerCase()
  if (host === 'localhost' || host.endsWith('.local') || host.endsWith('.lan')) return true

  const octets = host.split('.')
  if (octets.length === 4 && octets.every((o) => /^\d+$/.test(o))) {
    const [a, b] = octets.map(Number)
    return a === 10 || a === 127 || (a === 172 && b >= 16 && b <= 31) || (a === 192 && b === 168)
  }
  return !host.includes('.')
}

/** Refreshed half a minute early, to cover clock skew and a slow link. */
function needsRefresh(session: Session): boolean {
  return new Date(session.accessTokenExpiresAt).getTime() - Date.now() < 30_000
}

/**
 * Held while a refresh is in flight so concurrent calls share one.
 *
 * Without it, a dashboard that fires six requests on load would start six refreshes; the first
 * rotates the token and the rest present the rotated value, which the server correctly reads as a
 * replay and answers by revoking every session. It shows up as random sign-outs.
 */
let inFlightRefresh: Promise<Session | null> | null = null

async function refresh(
  session: Session,
  { force = false }: { force?: boolean } = {},
): Promise<Session | null> {
  // A refresh already running is the one to wait for, forced or not: it is about to produce the
  // token this caller wants, and starting a second would present the same refresh token twice,
  // which the server correctly reads as a replay.
  if (inFlightRefresh) return inFlightRefresh

  // The caller that came here on a 401 holds a token the server has already rejected, so the
  // stored session may be newer than what it was sent with.
  const current = force ? (storedSession() ?? session) : session

  inFlightRefresh = (async () => {
    try {
      const response = await fetch(`${storedServer()}/api/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: current.refreshToken }),
      })
      if (!response.ok) {
        // An explicit rejection: the refresh token is spent, revoked, or was issued by a server
        // that has since been rebuilt. None of those are recoverable by asking again.
        endSession()
        return null
      }
      const next = (await response.json()) as Session
      setSession(next)
      return next
    } catch {
      // A refresh that fails because the network is down must not sign anybody out. Only an
      // explicit rejection from the server ends a session.
      return current
    } finally {
      inFlightRefresh = null
    }
  })()

  return inFlightRefresh
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** Sign-in and the server probe must not carry a token, or signing in would need one. */
  anonymous?: boolean
  /** Return the body as text rather than parsing it, for a file such as a KML export. */
  text?: boolean
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<Result<T>> {
  const base = storedServer()
  if (!base) {
    return { ok: false, error: { code: 'NO_SERVER', message: 'No server is configured.', status: 0 } }
  }

  let session = storedSession()
  if (!options.anonymous && session && needsRefresh(session)) {
    session = await refresh(session)
  }

  const send = async (bearer: Session | null): Promise<Response | null> => {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' }
    if (!options.anonymous && bearer) headers.Authorization = `Bearer ${bearer.accessToken}`
    try {
      return await fetch(`${base}${path}`, {
        method: options.method ?? 'GET',
        headers,
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
      })
    } catch {
      return null
    }
  }

  let response = await send(session)
  if (response === null) {
    return {
      ok: false,
      error: {
        code: 'NETWORK',
        message: 'Could not reach the server. Check it is running and the address is right.',
        status: 0,
      },
    }
  }

  // A 401 on a token we believed was valid. The clock check above only catches expiry we can
  // predict; it cannot know the token was revoked, or that the server was rebuilt with a new
  // signing key. So refresh once regardless of the clock, and replay.
  if (response.status === 401 && !options.anonymous && session) {
    const renewed = await refresh(session, { force: true })

    // Three outcomes, and they must not be collapsed into two. `null` is the server explicitly
    // refusing, which has already ended the session. The same token back means the refresh could
    // not be made at all -- the network is down -- and somebody at a game site with no signal must
    // keep their session, or a problem that fixes itself would lock them out of the event they are
    // running. Only a genuinely new token is worth replaying with.
    const refreshFailedOffline = renewed !== null && renewed.accessToken === session.accessToken

    if (renewed !== null && !refreshFailedOffline) {
      const replayed = await send(renewed)
      if (replayed === null) {
        return {
          ok: false,
          error: {
            code: 'NETWORK',
            message: 'Could not reach the server. Check it is running and the address is right.',
            status: 0,
          },
        }
      }
      response = replayed

      // Refused again with a token minted seconds ago. Refreshing is not the problem, and
      // continuing to poll with credentials the server rejects is what filled the log with 401s.
      if (response.status === 401) endSession()
    }
  }

  const requestId = response.headers.get('x-request-id') ?? undefined

  if (response.status === 204) return { ok: true, value: undefined as T }

  const text = await response.text()
  const parsed: unknown = text ? safeJson(text) : null

  if (response.ok) return { ok: true, value: (options.text ? text : parsed) as T }

  const body = (parsed ?? {}) as Record<string, unknown>
  return {
    ok: false,
    error: {
      code: typeof body.code === 'string' ? body.code : 'UNKNOWN',
      message:
        typeof body.message === 'string' ? body.message : `The server answered ${response.status}.`,
      status: response.status,
      requestId: typeof body.requestId === 'string' ? body.requestId : requestId,
      violations: violationsOf(body.violations),
    },
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

function violationsOf(raw: unknown): Record<string, string> | undefined {
  if (!Array.isArray(raw) || raw.length === 0) return undefined
  const out: Record<string, string> = {}
  for (const entry of raw) {
    const violation = entry as { field?: unknown; message?: unknown }
    if (typeof violation.field === 'string' && typeof violation.message === 'string') {
      out[violation.field] = violation.message
    }
  }
  return Object.keys(out).length ? out : undefined
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  text: (path: string) => request<string>(path, { text: true }),
}
